// @flow

import type { ComponentType } from 'react';
import type { Descriptor } from '../../../controls/router/createDescriptor';
import type { AggregatesListingsFilters_root } from '../__generated__/AggregatesListingsFilters_root.graphql';
import { RoomsFilter } from './RoomsFilter';
import {
  roomsFilterDescriptor,
  landSurfaceFilterDescriptor,
  livingSurfaceFilterDescriptor,
  rentPricePerAdditionalDescriptor,
  salePricePerLivingSurfaceDescriptor,
  salePricePerLandSurfaceDescriptor,
} from './sharedDescriptors';

import { LandSurface, SurfaceFilter } from './SurfaceFilter';
import {
  PricePerLandSurface,
  PricePerLivingSurface,
  RentPricePerM2Year,
  RentPricePerRoom,
} from './PriceFilter';

type Props = {|
  root: AggregatesListingsFilters_root,
  inline?: boolean,
  shouldFlush: boolean,
|};

type Filter = {|
  component: ComponentType<Props>,
  descriptor: Descriptor<any>,
|};

const FILTERS: { [string]: Filter, ... } = {
  Rooms: { component: RoomsFilter, descriptor: roomsFilterDescriptor },
  Surface: {
    component: SurfaceFilter,
    descriptor: livingSurfaceFilterDescriptor,
  },
  LandSurface: {
    component: LandSurface,
    descriptor: landSurfaceFilterDescriptor,
  },
  PricePerLivingSurface: {
    component: PricePerLivingSurface,
    descriptor: salePricePerLivingSurfaceDescriptor,
  },
  PricePerLandSurface: {
    component: PricePerLandSurface,
    descriptor: salePricePerLandSurfaceDescriptor,
  },
  RentPricePerM2Year: {
    component: RentPricePerM2Year,
    descriptor: rentPricePerAdditionalDescriptor,
  },
  RentPricePerRoom: {
    component: RentPricePerRoom,
    descriptor: rentPricePerAdditionalDescriptor,
  },
};

/*
If `other` is true the filters that are visible only
in the "Other" popup on desktop (`OtherFilter`).
On mobile view first the non-other filters are shown, then others.
 */
type PropertyFilters = $ReadOnlyArray<{|
  filterName: $Keys<typeof FILTERS>,
  offer: 'all' | 'buy' | 'rent',
  other?: boolean,
|}>;

const DEFAULT_PROPERTY_TYPE_FILTERS: PropertyFilters = [
  { filterName: 'Surface', offer: 'all' },
  { filterName: 'LandSurface', offer: 'all', other: true },
  { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
  { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
  { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
  { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
];

const PROPERTY_TYPES_FILTERS: { [string]: PropertyFilters, ... } = {
  HOUSE_APPT: [
    { filterName: 'Rooms', offer: 'all' },
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'LandSurface', offer: 'all', other: true },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  HOUSE: [
    { filterName: 'Rooms', offer: 'all' },
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'LandSurface', offer: 'all', other: true },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  APPT: [
    { filterName: 'Rooms', offer: 'all' },
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  ROOM: [
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  PARK: [],
  PROP: [
    { filterName: 'LandSurface', offer: 'all' },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  BUILDING: [
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'LandSurface', offer: 'all', other: true },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  COMMERCIAL: [
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'LandSurface', offer: 'all', other: true },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  GASTRO: [
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'LandSurface', offer: 'all', other: true },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'PricePerLandSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
  OTHER: [
    { filterName: 'Surface', offer: 'all' },
    { filterName: 'PricePerLivingSurface', offer: 'buy', other: true },
    { filterName: 'RentPricePerM2Year', offer: 'rent', other: true },
    { filterName: 'RentPricePerRoom', offer: 'rent', other: true },
  ],
};

const getPropertyTypeFilters = (propertyMainType: null | string) =>
  propertyMainType == null ||
  typeof PROPERTY_TYPES_FILTERS[propertyMainType] === 'undefined'
    ? DEFAULT_PROPERTY_TYPE_FILTERS
    : PROPERTY_TYPES_FILTERS[propertyMainType];

export const getFiltersByPropertyMainType = (
  propertyMainType: null | string,
  offerType: 'all' | 'buy' | 'rent',
  other?: boolean,
): Array<ComponentType<Props>> =>
  getPropertyTypeFilters(propertyMainType)
    .filter(
      propertyFilter =>
        propertyFilter.offer === offerType && propertyFilter.other === other,
    )
    .map(({ filterName }) => FILTERS[filterName].component);

export const getDescriptorsByPropertyMainType = (
  propertyMainType: null | string,
  offerType: 'all' | 'buy' | 'rent',
  other?: boolean,
): Array<Descriptor<any>> =>
  getPropertyTypeFilters(propertyMainType)
    .filter(
      propertyFilter =>
        propertyFilter.offer === offerType && propertyFilter.other === other,
    )
    .map(({ filterName }) => FILTERS[filterName].descriptor);

export const getPropertyTypesAllDescriptors = (): Array<Descriptor<any>> =>
  getPropertyTypeFilters(null).map(
    ({ filterName: filterName }) => FILTERS[filterName].descriptor,
  );

export const paramsToReset = (propertyMainType: null | string): { ... } => {
  if (
    propertyMainType == null ||
    typeof PROPERTY_TYPES_FILTERS[propertyMainType] === 'undefined'
  ) {
    return {};
  }

  const propertyFiltersNames = PROPERTY_TYPES_FILTERS[propertyMainType].map(
    ({ filterName }) => filterName,
  );

  return Object.keys(FILTERS).reduce((params, filterName) => {
    if (!propertyFiltersNames.includes(filterName)) {
      params = {
        ...params,
        ...FILTERS[filterName].descriptor.defaults,
      };
    }

    return params;
  }, {});
};
